import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, CheckIcon, PlusCircleIcon, EyeIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';
import ProjectPlanDetail from './ProjectPlanDetail';

const ClientProjectPlan = ({ clientId, clientName }) => {
  const [loading, setLoading] = useState(true);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectPlans, setProjectPlans] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [selectedFieldDefinitions, setSelectedFieldDefinitions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [viewingPlanId, setViewingPlanId] = useState(null);
  
  // New plan form state
  const [newPlanName, setNewPlanName] = useState('');
  // Changed from fieldValues object to an array of rows, each containing fieldValues
  const [rows, setRows] = useState([{ id: 0, fieldValues: {} }]);
  
  // Fetch field definitions
  const fetchFieldDefinitions = async () => {
    setFieldsLoading(true);
    try {
      const response = await api.get('/settings/project-fields');
      // Filter to only active fields and sort by display order
      const activeFields = (response.data.fields || [])
        .filter(field => field.is_active)
        .sort((a, b) => a.display_order - b.display_order);
      
      setFieldDefinitions(activeFields);
      
      // Set all active fields as selected by default
      setSelectedFieldDefinitions(activeFields.map(field => field.id));
      
      // Initialize field values for the first row
      resetForm();
    } catch (err) {
      console.error('Error fetching field definitions:', err);
      toast.error('Failed to load field definitions');
      setError('Failed to load field definitions');
    } finally {
      setFieldsLoading(false);
    }
  };
  
  // Fetch project plans for this client
  const fetchProjectPlans = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clients/${clientId}/project-plans`);
      setProjectPlans(response.data.plans || []);
    } catch (err) {
      console.error('Error fetching project plans:', err);
      toast.error('Failed to load project plans');
      setError('Failed to load project plans');
    } finally {
      setLoading(false);
    }
  };
  
  // Initialize
  useEffect(() => {
    fetchFieldDefinitions();
    fetchProjectPlans();
  }, [clientId]);
  
  // Reset form
  const resetForm = () => {
    setNewPlanName('');
    // Create a single initial row with empty values
    const initialValues = {};
    if (fieldDefinitions.length > 0) {
      fieldDefinitions.forEach(field => {
        if (selectedFieldDefinitions.includes(field.id)) {
          initialValues[field.id] = '';
        }
      });
    }
    setRows([{ id: 0, fieldValues: initialValues }]);
    setEditingPlanId(null);
  };
  
  // Handle field value change for a specific row
  const handleFieldValueChange = (rowId, fieldId, value) => {
    setRows(prevRows => {
      return prevRows.map(row => 
        row.id === rowId 
          ? { 
              ...row, 
              fieldValues: { 
                ...row.fieldValues, 
                [fieldId]: value 
              } 
            }
          : row
      );
    });
  };
  
  // Add a new row
  const addRow = () => {
    setRows(prevRows => {
      // Generate a new unique row ID
      const newRowId = Math.max(0, ...prevRows.map(row => row.id)) + 1;
      
      // Create empty field values for new row
      const initialValues = {};
      fieldDefinitions.forEach(field => {
        if (selectedFieldDefinitions.includes(field.id)) {
          initialValues[field.id] = '';
        }
      });
      
      return [...prevRows, { id: newRowId, fieldValues: initialValues }];
    });
  };
  
  // Remove a row
  const removeRow = (rowId) => {
    // Don't remove if it's the last row
    if (rows.length <= 1) {
      toast.error('Cannot remove the last row');
      return;
    }
    
    setRows(prevRows => prevRows.filter(row => row.id !== rowId));
  };
  
  // Handle toggling field selection
  const handleFieldSelectionToggle = (fieldId) => {
    const isSelected = selectedFieldDefinitions.includes(fieldId);
    let newSelectedFields;
    
    if (isSelected) {
      // Remove from selected fields
      newSelectedFields = selectedFieldDefinitions.filter(id => id !== fieldId);
    } else {
      // Add to selected fields
      newSelectedFields = [...selectedFieldDefinitions, fieldId];
    }
    
    setSelectedFieldDefinitions(newSelectedFields);
    
    // Update row field values
    setRows(prevRows => {
      return prevRows.map(row => {
        const updatedFieldValues = {...row.fieldValues};
        
        if (isSelected) {
          // Field was deselected, remove it from values
          delete updatedFieldValues[fieldId];
        } else {
          // Field was selected, add it with empty value
          updatedFieldValues[fieldId] = '';
        }
        
        return {
          ...row,
          fieldValues: updatedFieldValues
        };
      });
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPlanName.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    if (selectedFieldDefinitions.length === 0) {
      toast.error('At least one field must be selected');
      return;
    }
    
    // Check required fields for all rows
    let hasErrors = false;
    rows.forEach((row, index) => {
      const missingRequiredFields = fieldDefinitions
        .filter(field => 
          selectedFieldDefinitions.includes(field.id) && 
          field.is_required && 
          !row.fieldValues[field.id]
        )
        .map(field => field.name);
      
      if (missingRequiredFields.length > 0) {
        toast.error(`Row ${index + 1}: Missing required fields: ${missingRequiredFields.join(', ')}`);
        hasErrors = true;
      }
    });
    
    if (hasErrors) return;
    
    try {
      // Transform rows and field values to the format expected by the API
      const formattedRows = rows.map((row, index) => {
        const fieldValuesList = Object.entries(row.fieldValues)
          .filter(([fieldId]) => selectedFieldDefinitions.includes(parseInt(fieldId, 10)))
          .map(([fieldId, value]) => ({
            field_definition_id: parseInt(fieldId, 10),
            value_text: value
          }));
        
        return {
          row_number: index + 1,
          field_values: fieldValuesList
        };
      });
      
      const planData = {
        name: newPlanName,
        rows: formattedRows,
        field_definition_ids: selectedFieldDefinitions
      };
      
      if (editingPlanId) {
        // Update existing plan
        await api.put(`/clients/${clientId}/project-plans/${editingPlanId}`, planData);
        toast.success('Project plan updated successfully');
      } else {
        // Create new plan
        await api.post(`/clients/${clientId}/project-plans`, planData);
        toast.success('Project plan created successfully');
      }
      
      // Refresh data and reset form
      fetchProjectPlans();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving project plan:', err);
      toast.error('Failed to save project plan');
    }
  };

  // Handle edit plan
  const handleEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setNewPlanName(plan.name);
    
    // Set the selected field definitions
    if (plan.field_definitions && plan.field_definitions.length > 0) {
      setSelectedFieldDefinitions(
        plan.field_definitions.map(fd => fd.field_definition_id)
      );
    } else if (plan.rows && plan.rows.length > 0 && plan.rows[0].field_values) {
      // Fallback: extract field definitions from field values in first row
      const uniqueFieldIds = [...new Set(
        plan.rows.flatMap(row => 
          row.field_values.map(fv => fv.field_definition_id)
        )
      )];
      setSelectedFieldDefinitions(uniqueFieldIds);
    } else {
      // Reset to all fields if nothing is available
      setSelectedFieldDefinitions(fieldDefinitions.map(fd => fd.id));
    }
    
    if (plan.rows && plan.rows.length > 0) {
      // Handle new format with rows
      const formattedRows = plan.rows.map(row => {
        const rowFieldValues = {};
        
        if (row.field_values) {
          row.field_values.forEach(fieldValue => {
            rowFieldValues[fieldValue.field_definition_id] = fieldValue.value_text || '';
          });
        }
        
        return {
          id: row.id || row.row_number,
          fieldValues: rowFieldValues
        };
      });
      
      setRows(formattedRows);
    } else if (plan.field_values) {
      // Backward compatibility: Handle old format without rows
      const values = {};
      plan.field_values.forEach(fieldValue => {
        values[fieldValue.field_definition_id] = fieldValue.value_text || '';
      });
      
      setRows([{ id: 0, fieldValues: values }]);
    } else {
      // No data, set empty form
      resetForm();
    }
    
    setShowAddForm(true);
  };
  
  // Handle delete plan
  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this project plan?')) {
      return;
    }
    
    try {
      await api.delete(`/clients/${clientId}/project-plans/${planId}`);
      toast.success('Project plan deleted successfully');
      fetchProjectPlans();
    } catch (err) {
      console.error('Error deleting project plan:', err);
      toast.error('Failed to delete project plan');
    }
  };
  
  // Handle mark as completed
  const handleMarkCompleted = async (planId, isCompleted) => {
    try {
      await api.patch(`/clients/${clientId}/project-plans/${planId}`, {
        is_completed: !isCompleted
      });
      toast.success(`Project plan marked as ${isCompleted ? 'incomplete' : 'completed'}`);
      fetchProjectPlans();
    } catch (err) {
      console.error('Error updating project plan status:', err);
      toast.error('Failed to update project plan status');
    }
  };
  
  // Handle create project from plan
  const handleCreateProject = async (plan) => {
    try {
      await api.post(`/clients/${clientId}/project-plans/${plan.id}/create-project`);
      toast.success('Project created successfully');
      fetchProjectPlans();
    } catch (err) {
      console.error('Error creating project from plan:', err);
      toast.error('Failed to create project');
    }
  };
  
  // Add a handler to view plan details
  const handleViewPlan = (planId) => {
    setViewingPlanId(planId);
    // Close add form if it's open
    setShowAddForm(false);
  };
  
  // Handle back button from plan detail view
  const handleBackFromDetail = () => {
    setViewingPlanId(null);
    fetchProjectPlans(); // Refresh the list in case changes were made
  };
  
  // Handle plan deletion from detail view
  const handleDeleteFromDetail = () => {
    setViewingPlanId(null);
    fetchProjectPlans(); // Refresh the list after deletion
  };
  
  // Render field input based on field type
  const renderFieldInput = (rowId, field) => {
    const row = rows.find(r => r.id === rowId) || { fieldValues: {} };
    const value = row.fieldValues[field.id] || '';
    
    switch (field.field_type) {
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldValueChange(rowId, field.id, e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        );
      
      case 'numeric':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldValueChange(rowId, field.id, e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        );
      
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldValueChange(rowId, field.id, e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">Select an option</option>
            {field.options?.values?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      
      case 'text':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldValueChange(rowId, field.id, e.target.value)}
            rows={3}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        );
      
      // Default case for alphanumeric and any other types
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldValueChange(rowId, field.id, e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        );
    }
  };
  
  // Format field value for display
  const formatFieldValue = (field, value) => {
    if (!value) return '—';
    
    switch (field.field_type) {
      case 'date':
        return new Date(value).toLocaleDateString();
      default:
        return value;
    }
  };
  
  if (fieldsLoading) {
    return (
      <div className="py-6 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (error && !fieldDefinitions.length) {
    return (
      <div className="py-6 px-4 text-center text-red-500">
        <p>{error}</p>
        <button 
          onClick={() => {
            fetchFieldDefinitions();
            fetchProjectPlans();
          }}
          className="mt-2 text-indigo-600 hover:text-indigo-900"
        >
          Try again
        </button>
      </div>
    );
  }
  
  if (!fieldDefinitions.length) {
    return (
      <div className="py-6 px-4 text-center text-gray-500">
        <p>No project fields have been defined. Please create field definitions in Settings first.</p>
      </div>
    );
  }
  
  // If viewing a specific plan, render the detail view
  if (viewingPlanId) {
    return (
      <ProjectPlanDetail
        clientId={clientId}
        planId={viewingPlanId}
        clientName={clientName}
        onBack={handleBackFromDetail}
        onDelete={handleDeleteFromDetail}
      />
    );
  }
  
  return (
    <div className="py-6 px-4">
      {/* Header - Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 bg-indigo-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium leading-6 text-indigo-900">Project Plan</h3>
              <p className="mt-1 text-sm text-indigo-700">
                Plan and organize potential projects for {clientName}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 sm:flex-none">
              {!showAddForm && (
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Add Project Plan
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Project Plan Form - Indigo Theme */}
      {showAddForm && (
        <div className="mt-6 bg-white shadow sm:rounded-lg border-l-4 border-indigo-100">
          <div className="px-4 py-5 sm:p-6 bg-indigo-50">
            <h3 className="text-lg font-medium leading-6 text-indigo-900">
              {editingPlanId ? 'Edit Project Plan' : 'Add New Project Plan'}
            </h3>
            <form className="mt-5 space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="project-name" className="block text-sm font-medium text-gray-700">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="project-name"
                    id="project-name"
                    value={newPlanName}
                    onChange={(e) => setNewPlanName(e.target.value)}
                    required
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
              
              {/* Field selection */}
              <div>
                <h4 className="block text-sm font-medium text-gray-700 mb-2">
                  Select Fields to Include <span className="text-red-500">*</span>
                </h4>
                <div className="mt-1 border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-2 md:grid-cols-3">
                    {fieldDefinitions.map((field) => (
                      <div key={field.id} className="flex items-center">
                        <input
                          id={`field-selection-${field.id}`}
                          name={`field-selection-${field.id}`}
                          type="checkbox"
                          checked={selectedFieldDefinitions.includes(field.id)}
                          onChange={() => handleFieldSelectionToggle(field.id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label
                          htmlFor={`field-selection-${field.id}`}
                          className="ml-3 text-sm text-gray-700"
                        >
                          {field.name} {field.is_required && <span className="text-red-500">*</span>}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Select which fields to include in this project plan. Required fields are marked with an asterisk (*).
                  </p>
                </div>
              </div>
              
              {/* Rows of field inputs */}
              {rows.map((row, rowIndex) => (
                <div 
                  key={row.id} 
                  className="border border-gray-200 rounded-lg p-4 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-md font-medium text-gray-700">Row {rowIndex + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Remove row"
                      disabled={rows.length <= 1}
                    >
                      <TrashIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    {fieldDefinitions
                      .filter(field => selectedFieldDefinitions.includes(field.id))
                      .map((field) => (
                        <div 
                          key={field.id} 
                          className={field.field_type === 'text' ? 'sm:col-span-6' : 'sm:col-span-3'}
                        >
                          <label 
                            htmlFor={`field-${row.id}-${field.id}`} 
                            className="block text-sm font-medium text-gray-700"
                          >
                            {field.name} {field.is_required && <span className="text-red-500">*</span>}
                          </label>
                          <div className="mt-1">
                            {renderFieldInput(row.id, field)}
                          </div>
                          {field.description && (
                            <p className="mt-2 text-xs text-gray-500">{field.description}</p>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
              
              {/* Add Row button */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addRow}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Add Another Row
                </button>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {editingPlanId ? 'Update Plan' : 'Add Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Project Plans Table - Indigo Border */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg border-l-4 border-indigo-100">
              {loading ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
                  <p className="mt-2">Loading project plans...</p>
                </div>
              ) : projectPlans.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
                    <PlusCircleIcon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <p className="text-gray-700 font-medium">No project plans found.</p>
                  <p className="mt-2 text-sm text-gray-500">Click "Add Project Plan" to create your first plan.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Project Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Fields
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Rows
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {projectPlans.map(plan => {
                      // Count the number of rows
                      const rowCount = plan.rows?.length || (plan.field_values?.length > 0 ? 1 : 0);
                      
                      // Get list of field definitions for this plan
                      const planFieldIds = (plan.field_definitions || []).map(fd => fd.field_definition_id);
                      
                      // If no specific field_definitions property, try extracting from first row
                      const fallbackFieldIds = plan.rows && plan.rows.length > 0
                        ? [...new Set(plan.rows.flatMap(row => row.field_values.map(fv => fv.field_definition_id)))]
                        : [];
                        
                      const fieldIds = planFieldIds.length > 0 ? planFieldIds : fallbackFieldIds;
                      
                      return (
                        <tr 
                          key={plan.id} 
                          className={plan.is_completed ? 'bg-gray-50' : undefined}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {plan.name}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            <div className="flex flex-wrap gap-1">
                              {fieldIds.map(fieldId => {
                                const field = fieldDefinitions.find(f => f.id === fieldId);
                                return field ? (
                                  <span 
                                    key={fieldId}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                                  >
                                    {field.name}
                                  </span>
                                ) : null;
                              })}
                              {fieldIds.length === 0 && (
                                <span className="text-gray-400">No fields defined</span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span 
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                plan.is_completed
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {plan.is_completed ? 'Completed' : 'Pending'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {rowCount} {rowCount === 1 ? 'row' : 'rows'}
                            </span>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex justify-end space-x-3">
                              <button
                                onClick={() => handleViewPlan(plan.id)}
                                className="text-indigo-600 hover:text-indigo-900 font-semibold focus:outline-none focus:underline"
                                title="View plan details"
                              >
                                <EyeIcon className="h-5 w-5" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleMarkCompleted(plan.id, plan.is_completed)}
                                className={`$${
                                  plan.is_completed ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'
                                }`}
                                title={plan.is_completed ? 'Mark as incomplete' : 'Mark as completed'}
                              >
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleDeletePlan(plan.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete plan"
                              >
                                <TrashIcon className="h-5 w-5" aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProjectPlan; 